<template>
  <div
    :data-type="type"
    class="product-alternatives"
    :class="{
      ['product-alternatives--' + state]: true,
    }"
    v-if="products && products.length > 0 || state === 'LOADING' || state === 'FALLBACK'"
  >
    <div class="cinema-teaser recommendations-teaser">
      <div
        class="cinema-teaser__headline | headline headline--type_h2"
        v-html="headline"
      />
      <div class="cinema-teaser__items-container">
        <product-box-slider
          class="recommendations-tiles-teaser__products"
          :products="products"
          tracking-list="productAlternatives"
          :columns="4"
        />
      </div>
      <div
        class="cinema-teaser__link"
        v-if="linkUrl"
      >
        <a
          :href="linkUrl"
          class="button product-alternatives__button button--secondary button--secondary-arrow"
          v-text="$t('ads.main.recommendation.link.winecloseness')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {ref}  from 'vue';
  import ProductBoxSlider from '../productBoxSlider/productBoxSlider.vue';
  import ProductBoxData from '../../types/ProductBoxData';
  import productStore from '../../stores/product/ProductStore';
  import productPageService from '../../services/product/ProductPageService';
  import {TrackingService} from '../../services/tracking/TrackingService';
  import jsGlobalConfig from '../../globals/jsGlobalConfig';
  import pigeon from '../../utilities/js/pigeon/pigeon';

  export default {
    name: 'ProductAlternatives',
    components: {
      ProductBoxSlider
    },
    props: {
      sectionPosition: {
        type: String,
        required: true
      },
      removeSelectorIfEmpty: {
        type: String,
        required: false,
        default: null
      },
      useThirdPartyProducts: {
        type: Boolean,
        default: false
      }
    },

    setup(props) {
      let products = ref<ProductBoxData[]>([]);
      let headline = ref<string>('');
      let linkUrl = ref<string>('');
      let type = ref<string>('');
      let itemId = ref<string>(productStore.productDetails.product.itemId);
      let state = ref<'READY'|'LOADING'|'SUCCESS'|'ERROR'>('READY');
      pigeon.subscribe('productPage::variationChange', (data: any) => variationChangeAlternatives(data.itemId));

      updateProductAlternatives();

      function variationChangeAlternatives(updatedItemId) {
        // only on variation change trigger update ajax
        if (updatedItemId !== itemId.value) {
          itemId.value = updatedItemId;
          updateProductAlternatives();
        }
      }

      function updateProductAlternatives() {
        if (!jsGlobalConfig().getShowSoldoutAlternatives() && props.sectionPosition === 'bottom') {
          getProductAlternatives();
        } else if (
          jsGlobalConfig().getShowSoldoutAlternatives() && (
            props.sectionPosition === 'top' && isSoldout() ||
            props.sectionPosition === 'bottom' && !isSoldout()
          )
        ) {
          getProductAlternatives();
        } else {
          state.value = 'READY';
          products.value = [];
          headline.value = '';

          if (props.removeSelectorIfEmpty) {
            document.querySelector(props.removeSelectorIfEmpty)?.classList.add('product-detailed-page__section--hidden');
          }
        }
      }

      function isSoldout() {
        return productStore.productDetails.product.availability.status === 'NOT_AVAILABLE' && !productStore.productDetails.product.availability.subscriptionPreview;
      }

      function getProductAlternatives() {
        let trackingData = {};
        state.value = 'LOADING';

        const promise = props.useThirdPartyProducts
          ? productPageService.getThirdPartyProductAlternatives(productStore.productDetails.product.artNr)
          : productPageService.getProductAlternatives(productStore.productDetails.product.artNr);

        promise.then(response => {
          if (response.data.recommendationDataInfo) {
            headline.value = response.data.recommendationDataInfo.headline;
            type.value = response.data.recommendationDataInfo.type;

            if (response.data.recommendationDataInfo.type === 'SIMILAR' && response.data.hasMore) {
              linkUrl.value = `/fit/${productStore.productDetails.product.artNr}/`;
            }
          }

          if (response.data.productBoxData?.length > 0) {
            state.value = 'SUCCESS';
            trackingData = {
              event: 'productAlternatives.results.success',
              position: props.sectionPosition,
              articleNo: productStore.productDetails.product.artNr,
              length: response.data.productBoxData.length,
              products: response.data.productBoxData.map(element => element.trackingData)
            };
            products.value = response.data.productBoxData;
            TrackingService.trigger(TrackingService.EVENT_GENERIC, trackingData);

            if (props.removeSelectorIfEmpty) {
              document.querySelector(props.removeSelectorIfEmpty)?.classList.remove('product-detailed-page__section--hidden');
            }
          } else {
            state.value = 'ERROR';
            trackingData = {
              event: 'productAlternatives.results.empty',
              position: props.sectionPosition,
              articleNo: productStore.productDetails.product.artNr
            };
            TrackingService.trigger(TrackingService.EVENT_GENERIC, trackingData);

            if (props.removeSelectorIfEmpty) {
              document.querySelector(props.removeSelectorIfEmpty)?.classList.add('product-detailed-page__section--hidden');
            }
          }
        }).catch(() => {
          state.value = 'ERROR';

          if (props.removeSelectorIfEmpty) {
            document.querySelector(props.removeSelectorIfEmpty)?.classList.add('product-detailed-page__section--hidden');
          }
        }).finally(() => {
          pigeon.publish('productPage::sectionChange', {});
        });
      }

      return {
        headline,
        linkUrl,
        type,
        state,
        products
      }
    }
  }
</script>
